import React from 'react';
import WordpressArticle from '@gik/core/models/wordpress/WordpressArticle';
import bemBlock from '@gik/core/utils/bemBlock';
import { FontIcon } from '@gik/ui';
import SavedArticleCard from './SavedArticleCard';
import SavedArticlesListSkeleton from './SavedArticlesListSkeleton';
import { useUserStore } from '@gik/core/store/UserStore';
import { useSavedArticles } from '@gik/api/users/savedArticles';
import withSuspense from '@gik/core/components/suspended/withSuspense';
import { removeFavoriteArticle } from '@gik/api/users/user';
import { optimisticUpdate } from '@gik/core/api';

const blockName = 'saved-articles-list';

export interface ISavedArticlesListProps {
  items?: Array<WordpressArticle>;
}

function SavedArticlesList({ items }: ISavedArticlesListProps): React.ReactElement {
  const bem = bemBlock(blockName);

  const userStore = useUserStore();
  const { data: articles, error: articlesError, mutate } = useSavedArticles(userStore.id, {
    initialData: items,
  });

  async function handleDelete(article: WordpressArticle) {
    const newData = articles.filter(item => item.id !== article.id);
    optimisticUpdate(newData, mutate, () => removeFavoriteArticle(userStore.id, article.id));
  }

  const isLoading = !articles;
  const isError = isLoading && articlesError;

  if (isLoading) return <SavedArticlesListSkeleton />;
  if (isError) return <SavedArticlesListSkeleton />;

  if (articles?.length === 0) {
    return (
      <div className={bem(null, 'empty')}>
        <div className={bem('preview')} />
        <div className={bem('preview')}>
          <span className={bem('instructions')}>
            Click the <FontIcon name="heart-o" /> on Articles to save them here.
          </span>
        </div>
        <div className={bem('preview')} />
      </div>
    );
  }

  return (
    <ul className={bem('items')}>
      {articles?.map((item: WordpressArticle) => (
        <SavedArticleCard key={item.id} article={item} onRemove={handleDelete} />
      ))}
    </ul>
  );
}

export default withSuspense(SavedArticlesList, SavedArticlesListSkeleton);
